import React, { useState } from 'react';
import type { ApprovalSummary, BuildSummary, SpecSummary, SummaryVersion } from '../../../lib/aiJobSummary';
import { formatRelativeDate } from '../../../utils/dateUtils';
import { Badge, Button } from '../../ui';
import RequestTimeline from './RequestTimeline';
import { OpCard } from './SectionHeading';

/**
 * Versions tab: every summary ever written for the job, newest on top.
 * A build or approval version renders against the spec that was current
 * when it was written, so the page reads the way it did at the time.
 */

const STAGE_LABEL: Record<SummaryVersion['stage'], string> = { spec: 'Spec', build: 'Build', approval: 'Live' };
const STAGE_TONE: Record<SummaryVersion['stage'], 'accent' | 'warn' | 'up'> = { spec: 'accent', build: 'warn', approval: 'up' };

function latestBefore(versions: SummaryVersion[], stage: SummaryVersion['stage'], at: string): SummaryVersion | undefined {
  let found: SummaryVersion | undefined;
  for (const v of versions) {
    if (v.stage === stage && v.created_at <= at) found = v;
  }
  return found;
}

export default function SummaryVersions({ versions }: { versions: SummaryVersion[] }) {
  const ordered = [...versions].sort((a, b) => b.created_at.localeCompare(a.created_at));
  const latestByStage = new Map<SummaryVersion['stage'], string>();
  for (const v of ordered) if (!latestByStage.has(v.stage)) latestByStage.set(v.stage, v.id);
  const [open, setOpen] = useState<Set<string>>(() => new Set(ordered.slice(0, 1).map((v) => v.id)));

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (ordered.length === 0) {
    return (
      <OpCard tone="raised">
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>No summary versions yet.</span>
      </OpCard>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {ordered.map((v, i) => {
        const isCurrent = latestByStage.get(v.stage) === v.id;
        const expanded = open.has(v.id);
        const spec = v.stage === 'spec' ? (v.summary as SpecSummary) : (latestBefore(versions, 'spec', v.created_at)?.summary as SpecSummary | undefined);
        const build = v.stage === 'build' ? (v.summary as BuildSummary) : v.stage === 'approval' ? (latestBefore(versions, 'build', v.created_at)?.summary as BuildSummary | undefined) : undefined;
        const approval = v.stage === 'approval' ? (v.summary as ApprovalSummary) : undefined;
        return (
          <OpCard key={v.id} padding="14px 16px" style={{ opacity: isCurrent ? 1 : 0.92 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <span className="t-num" style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'], color: 'var(--text-body)' }}>
                v{v.job_version}
              </span>
              <Badge tone={STAGE_TONE[v.stage]}>{STAGE_LABEL[v.stage]}</Badge>
              {isCurrent ? <Badge tone="neutral">current</Badge> : null}
              <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
                {[v.written_by, formatRelativeDate(v.created_at)].filter(Boolean).join(' · ')}
              </span>
              {i === 0 ? (
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>newest</span>
              ) : null}
              <span style={{ flex: 1 }} />
              <Button size="sm" variant="ghost" icon={expanded ? 'chevron-down' : 'chevron-right'} onClick={() => toggle(v.id)}>
                {expanded ? 'Hide' : 'Show'}
              </Button>
            </div>
            {expanded ? (
              <div style={{ marginTop: '14px' }}>
                {spec ? (
                  <RequestTimeline spec={spec} build={build} approval={approval} />
                ) : (
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>
                    This {STAGE_LABEL[v.stage].toLowerCase()} summary has no spec summary to sit under.
                  </span>
                )}
              </div>
            ) : null}
          </OpCard>
        );
      })}
    </div>
  );
}
